import React, { useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useNotesStore, useUIStore } from '../store';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuShortcut, ContextMenuTrigger } from '../components/ui/context-menu';
import TableView from '../components/TableView';
import CalendarView from '../components/CalendarView';
import TimelineView from '../components/TimelineView';
import { FileText, Plus, Calendar, Hash, FolderOpen, LayoutGrid, List, Clock, Columns } from 'lucide-react';

export default function Home() {
  const [searchParams] = useSearchParams();
  const tagFilter = searchParams.get('tag');
  const starred = searchParams.get('starred');
  const { notes, fetchNotes, deleteNote, toggleStar, isLoading, currentView, setView } = useNotesStore();

  useEffect(() => {
    fetchNotes({ tag: tagFilter || undefined, starred: starred || undefined });
  }, [tagFilter, starred]);

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const views = [
    { id: 'notes', icon: LayoutGrid, label: 'Grid' },
    { id: 'table', icon: List, label: 'Table' },
    { id: 'calendar', icon: Calendar, label: 'Calendar' },
    { id: 'timeline', icon: Clock, label: 'Timeline' },
  ];

  if (isLoading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            {tagFilter ? <><Hash size={20} className="text-muted-foreground" /> {tagFilter}</> :
             starred ? <><FileText size={20} className="text-muted-foreground" /> Starred</> :
             'All Notes'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{notes.length} note{notes.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-muted rounded-md p-0.5">
            {views.map(v => (
              <Button key={v.id} variant={currentView === v.id ? 'secondary' : 'ghost'} size="icon" className="h-7 w-7" onClick={() => setView(v.id)}>
                <v.icon size={14} />
              </Button>
            ))}
          </div>
        </div>
      </div>

      {currentView === 'table' && <TableView />}
      {currentView === 'calendar' && <CalendarView />}
      {currentView === 'timeline' && <TimelineView />}

      {currentView === 'notes' && (
        notes.length === 0 ? (
          <div className="text-center py-16">
            <FileText size={48} className="mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-lg font-medium mb-2">No notes yet</h3>
            <p className="text-muted-foreground mb-4">Create your first note to get started</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {notes.map(note => (
              <ContextMenu key={note.id}>
                <ContextMenuTrigger asChild>
                  <Link to={`/note/${note.id}`}>
                    <Card className="group hover:shadow-md transition-all cursor-pointer h-full">
                      <CardHeader className="pb-2 flex flex-row items-start justify-between space-y-0">
                        <CardTitle className="text-base group-hover:text-primary transition-colors line-clamp-1">{note.title}</CardTitle>
                        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleStar(note.id); }} className={`p-1 rounded hover:bg-muted ${note.starred ? 'text-yellow-500' : 'text-muted-foreground'}`}>
                          <FileText size={14} fill={note.starred ? 'currentColor' : 'none'} />
                        </button>
                      </CardHeader>
                      <CardContent>
                        {note.content && <p className="text-sm text-muted-foreground line-clamp-3 mb-3">{note.content.replace(/[#*`>\[\]]/g, '').slice(0, 150)}...</p>}
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground flex items-center gap-1"><Calendar size={12} /> {formatDate(note.updated_at)}</span>
                          {note.tags?.length > 0 && (
                            <div className="flex gap-1">
                              {note.tags.slice(0, 2).map(t => <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>)}
                              {note.tags.length > 2 && <Badge variant="secondary" className="text-xs">+{note.tags.length - 2}</Badge>}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </ContextMenuTrigger>
                <ContextMenuContent className="w-48">
                  <ContextMenuItem onClick={() => toggleStar(note.id)}>{note.starred ? 'Unstar' : 'Star'}</ContextMenuItem>
                  <ContextMenuItem onClick={() => navigator.clipboard.writeText(note.title)}>Copy Title</ContextMenuItem>
                  <ContextMenuSeparator />
                  <ContextMenuItem className="text-destructive" onClick={() => { if (confirm('Delete?')) deleteNote(note.id); }}>Delete</ContextMenuItem>
                </ContextMenuContent>
              </ContextMenu>
            ))}
          </div>
        )
      )}
    </div>
  );
}
